import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'audience-customers-drawer' }

import { ref, computed, watch, onBeforeUnmount, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatPhoneDisplay } from '@/util/phoneFormatter'
import { reasonLabel } from '@/util/campaignSuppressionReasons'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  show: { type: Boolean, default: false },
  // Either a saved audience id or a raw filter object; mirrors the lambda body
  audienceId: { type: Number as PropType<number | null>, default: null },
  audienceFilter: { type: Object as PropType<Record<string, any> | null>, default: null },
  // Ongoing campaign: list the reach (everyone who will be emailed between
  // today and endsOn), not today's slice. The caller passes the merged filter.
  reach: { type: Boolean, default: false },
  endsOn: { type: String, default: '' }
},
  emits: ['update:show', 'close'],
  setup(__props, { emit }) {

const props = __props

const PER_PAGE = 50
const EXPORT_PER_PAGE = 100





const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const customers = ref<any[]>([])
const total = ref(0)
const page = ref(1)
const loading = ref(false)
const error = ref('')

const downloading = ref(false)
// all / deliverable / undeliverable, mirrored to the lambda's mode
const mode = ref<'all' | 'deliverable' | 'undeliverable'>('all')

const hasMore = computed(() => customers.value.length < total.value)
const hasDownloadPermission = computed(() => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((p: string) => p === 'download_data')
})
const sectionTitle = computed(() => {
  if (!total.value) return props.reach ? 'Customers' : 'Recipients'
  const n = total.value.toLocaleString()
  if (mode.value === 'undeliverable') return `${n} undeliverable`
  if (mode.value === 'deliverable') return `${n} deliverable`
  return props.reach ? `${n} ${total.value === 1 ? 'customer' : 'customers'}, one email each on their day` : `${n} recipients`
})
const emptyText = computed(() => {
  if (mode.value === 'undeliverable') return 'No known undeliverable addresses in this audience'
  if (mode.value === 'deliverable') return 'No deliverable addresses in this audience'
  return 'No customers match this audience'
})

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function apiErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed?.error) return parsed.error
    } catch {
      if (typeof data === 'string' && data.length < 200) return data
    }
  }
  return fallback
}

function displayName(customer: any): string {
  return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email || ''
}

function requestBody(nextPage: number, perPage: number): Record<string, any> {
  const body: Record<string, any> = { page: nextPage, per_page: perPage }
  if (mode.value !== 'all') body.mode = mode.value
  if (props.reach) {
    body.audience_filter = props.audienceFilter || {}
    body.estimate_reach = true
    body.ends_on = props.endsOn || null
  } else if (props.audienceId) body.audience_id = props.audienceId
  else body.audience_filter = props.audienceFilter || {}
  return body
}

async function fetchPage(nextPage: number) {
  loading.value = true
  error.value = ''
  try {
    const config = await authConfig()
    const response = await axios.post(`${baseUrl}/email-campaigns/audience-preview`, requestBody(nextPage, PER_PAGE), config)
    total.value = response.data.count || 0
    customers.value = nextPage === 1
      ? (response.data.customers || [])
      : [...customers.value, ...(response.data.customers || [])]
    page.value = nextPage
  } catch (err: any) {
    error.value = apiErrorMessage(err, 'Could not load the recipients')
  } finally {
    loading.value = false
  }
}

// Walks every page (100 per request, the lambda's cap) so the file holds the
// whole audience, not just what has been loaded into the drawer.
async function downloadCSV() {
  if (downloading.value) return
  downloading.value = true
  try {
    const config = await authConfig()
    const all: any[] = []
    let nextPage = 1
    while (true) {
      const response = await axios.post(`${baseUrl}/email-campaigns/audience-preview`, requestBody(nextPage, EXPORT_PER_PAGE), config)
      const rows = response.data.customers || []
      all.push(...rows)
      if (rows.length < EXPORT_PER_PAGE || all.length >= (response.data.count || 0)) break
      nextPage += 1
    }
    const esc = (v: any) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
    let csv = 'First name,Last name,Email,Phone,Undeliverable\n'
    all.forEach((c) => {
      csv += [esc(c.first_name), esc(c.last_name), esc(c.email), esc(formatPhoneDisplay(c.phone, c.country || 'US')), esc(c.undeliverable_reason ? reasonLabel(c.undeliverable_reason) : '')].join(',') + '\n'
    })
    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = mode.value === 'all' ? 'audience-recipients.csv' : `audience-${mode.value}.csv`
    anchor.click()
  } catch (err: any) {
    error.value = apiErrorMessage(err, 'Could not download the recipients')
  } finally {
    downloading.value = false
  }
}

function loadMore() {
  if (!loading.value && hasMore.value) fetchPage(page.value + 1)
}

function reset() {
  customers.value = []
  total.value = 0
  page.value = 1
  error.value = ''
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    mode.value = 'all'
    reset()
    fetchPage(1)
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

watch(mode, () => {
  if (!props.show) return
  reset()
  fetchPage(1)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,PER_PAGE, EXPORT_PER_PAGE, props, emit, companyId, companyGroupId, baseUrl, customers, total, page, loading, error, downloading, mode, hasMore, hasDownloadPermission, sectionTitle, emptyText, authConfig, apiErrorMessage, displayName, requestBody, fetchPage, downloadCSV, loadMore, reset, onClose, onEscKey, formatPhoneDisplay, reasonLabel }
}

})